import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';

import type { FirstParameters } from '@looko-corp/acloset-react-kit/src/utils';

import type { RequestParams } from '@/Swagger';
import type { UseAxiosQueryOptions } from '@/utils/typeHelper';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AxiosResponseType = AxiosResponse<unknown, any>;

/**
 * Swagger API 의 useQuery wrapper.
 *
 * @example
 * useSwaggerQuery(aclosetApi.sendbird.tokenList, 'sendbird');
 *
 * @param queryFn  Swagger 함수.
 * @param baseKey Query 키.
 * @param params Request 인자.
 * @param option Query 옵션.
 */
export function useSwaggerQuery0<
  QueryFn extends (params?: RequestParams) => Promise<AxiosResponseType>,
  TQueryKey extends QueryKey,
>(
  queryFn: QueryFn,
  baseKey: TQueryKey,
  option?: Partial<UseAxiosQueryOptions<Awaited<ReturnType<QueryFn>>['data'], TQueryKey>>,
) {
  return useQuery({
    queryKey: baseKey,

    queryFn: async () => {
      const v = await queryFn();
      return v.data;
    },
    ...option,
  });
}

/**
 * useSwaggerQuery0 의 suspense 버전.
 */
export function useSuspenseSwaggerQuery0<
  QueryFn extends (params?: RequestParams) => Promise<AxiosResponseType>,
  TQueryKey extends QueryKey,
>(
  queryFn: QueryFn,
  baseKey: TQueryKey,
  option?: UseAxiosQueryOptions<Awaited<ReturnType<QueryFn>>['data'], TQueryKey>,
) {
  return useSuspenseQuery({
    queryKey: baseKey,
    queryFn: async () => {
      const v = await queryFn();
      return v.data;
    },
    ...option,
  });
}

/**
 * useSwaggerQuery0 의 인자 1개 버전.
 *
 * @see useSwaggerQuery0
 *
 * @example
 * useSwaggerQuery1(aclosetApi.sendbird.tokenList, 'sendbird', data1);
 */
export function useSwaggerQuery1<
  Var1Type,
  QueryFn extends (var1: Var1Type, params?: RequestParams) => Promise<AxiosResponseType>,
  TQueryKey extends QueryKey,
>(
  queryFn: QueryFn,
  keyFactory: (var1: Var1Type) => Readonly<[...TQueryKey, Var1Type]>,
  var1: Var1Type,
  option?: Partial<UseAxiosQueryOptions<Awaited<ReturnType<QueryFn>>['data'], Readonly<[...TQueryKey, Var1Type]>>>,
) {
  return useQuery({
    queryKey: keyFactory(var1),
    queryFn: async ({ queryKey }) => {
      const var1 = queryKey[queryKey.length - 1] as Var1Type;
      const v = await queryFn(var1);
      return v.data;
    },
    ...option,
  });
}

/**
 * useSwaggerQuery1 의 suspense 버전.
 */
export function useSuspenseSwaggerQuery1<
  Var1Type,
  QueryFn extends (var1: Var1Type, params?: RequestParams) => Promise<AxiosResponseType>,
  TQueryKey extends QueryKey,
>(
  queryFn: QueryFn,
  keyFactory: (var1: Var1Type) => Readonly<[...TQueryKey, Var1Type]>,
  var1: Var1Type,
  option?: UseAxiosQueryOptions<Awaited<ReturnType<QueryFn>>['data'], Readonly<[...TQueryKey, Var1Type]>>,
) {
  return useSuspenseQuery({
    queryKey: keyFactory(var1),
    queryFn: async ({ queryKey }) => {
      const var1 = queryKey[queryKey.length - 1] as Var1Type;
      const v = await queryFn(var1);
      return v.data;
    },
    ...option,
  });
}

/**
 * useSwaggerQuery0 의 인자 2개 버전.
 *
 * @see useSwaggerQuery0
 *
 * @example
 * useSwaggerQuery2(aclosetApi.sendbird.tokenList, 'sendbird', data1, data2);
 */
export function useSwaggerQuery2<
  Var1Type,
  Var2Type,
  QueryFn extends (var1: Var1Type, var2: Var2Type, params?: RequestParams) => Promise<AxiosResponseType>,
  TQueryKey extends QueryKey,
>(
  queryFn: QueryFn,
  keyFactory: (var1: Var1Type, var2: Var2Type) => Readonly<[...TQueryKey, Var1Type, Var2Type]>,
  var1: Var1Type,
  var2: Var2Type,
  option?: UseAxiosQueryOptions<Awaited<ReturnType<QueryFn>>['data'], Readonly<[...TQueryKey, Var1Type, Var2Type]>>,
) {
  return useQuery({
    queryKey: keyFactory(var1, var2),

    queryFn: async ({ queryKey }) => {
      const var1 = queryKey[queryKey.length - 2] as Var1Type;
      const var2 = queryKey[queryKey.length - 1] as Var2Type;
      const v = await queryFn(var1, var2);
      return v.data;
    },
    ...option,
  });
}

// ************************************************************************************************

/**
 * Swagger API 의 useMutation wrapper.
 *
 * @example
 * useSwaggerMutation(aclosetApi.sendbird.tokenCreate);
 *
 * @param queryFn  Swagger 함수.
 */
export function useSwaggerMutation(queryFn: (params?: RequestParams) => Promise<AxiosResponseType>) {
  return useMutation({
    mutationFn: async data => {
      const v = await queryFn();
      return v.data;
    },
  });
}

export function useSwaggerMutation1<Var1Type, ResponseType>(
  queryFn: (var1: Var1Type, params?: RequestParams) => Promise<AxiosResponse<ResponseType, any>>,
) {
  return useMutation<ResponseType, AxiosError, Var1Type>({
    mutationFn: async data => {
      const v = await queryFn(data);
      return v.data;
    },
  });
}

export function useSwaggerMutation2<Var1Type, Var2Type, ResponseType>(
  queryFn: (var1: Var1Type, var2: Var2Type, params?: RequestParams) => Promise<AxiosResponse<ResponseType, any>>,
) {
  return useMutation<ResponseType, AxiosError, [Var1Type, Var2Type]>({
    mutationFn: async data => {
      const v = await queryFn(...data);
      return v.data;
    },
  });
}
